import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { companyService } from '@/services/company.service'
import { companyMemberService } from '@/services/company_member.service'
import { maskCnpj } from '@/lib/validators'
import type { Company } from '@/types/company.types'
import type { CompanyMember } from '@/types/company_member.types'
import { IconUser } from '@/components/Icons'

// Components
import { Page } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import { Button } from '@/components/ui/Button'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { AlertModal } from '@/components/ui/AlertModal'

const ROLE_LABELS: Record<string, string> = {
  reader: 'Leitor', editor: 'Editor', admin: 'Administrador', owner: 'Proprietário',
}

// Icons
function IconPlus() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  )
}

function IconEditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  )
}

function IconTrash() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
      <line x1="10" y1="11" x2="10" y2="17"></line>
      <line x1="14" y1="11" x2="14" y2="17"></line>
    </svg>
  )
}

export function CompanyDetailPage() {
  const { companyId } = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const [company, setCompany] = useState<Company | null>(
    (location.state as { company?: Company } | null)?.company ?? null
  )
  const [loadingCompany, setLoadingCompany] = useState(!company)
  const [members, setMembers] = useState<CompanyMember[]>([])
  const [loadingMembers, setLoadingMembers] = useState(true)

  const [memberToDelete, setMemberToDelete] = useState<CompanyMember | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; message: string } | null>(null)

  useEffect(() => {
    if (company) return
    setLoadingCompany(true)
    companyService.list()
      .then(list => {
        const found = list.find(c => c.id === companyId)
        if (found) setCompany(found)
        else navigate('/dashboard/companies', { replace: true })
      })
      .finally(() => setLoadingCompany(false))
  }, [companyId])

  function fetchMembers() {
    if (!companyId) return
    setLoadingMembers(true)
    companyMemberService.list(companyId)
      .then(setMembers)
      .finally(() => setLoadingMembers(false))
  }

  useEffect(() => {
    fetchMembers()
  }, [companyId])

  async function executeDelete() {
    if (!companyId || !memberToDelete) return

    try {
      setDeleting(true)
      await companyMemberService.remove(companyId, memberToDelete.id)
      setMemberToDelete(null)
      fetchMembers()
    } catch (err: any) {
      console.error(err)
      setAlertConfig({
        isOpen: true,
        message: err.response?.data?.message || err.message || 'Erro ao remover usuário.'
      })
    } finally {
      setDeleting(false)
    }
  }

  if (loadingCompany) {
    return (
      <Page>
        <div className="ui-empty-state">Carregando...</div>
      </Page>
    )
  }
  if (!company) return null

  return (
    <Page>
      <Page.Header
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
            {company.name}
            {company.is_master && <span className="ui-badge ui-badge--green" style={{ fontSize: '12px' }}>Master</span>}
          </span>
        }
        subtitle={
          <span style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
            <span>CNPJ: <strong>{maskCnpj(company.cnpj || '')}</strong></span>
            <span style={{ color: 'var(--border-strong)' }}>·</span>
            <span>Cadastrada em: <strong>{new Date(company.created_at).toLocaleDateString('pt-BR')}</strong></span>
          </span>
        }
        onBack={() => navigate('/dashboard/companies')}
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {Boolean(company.is_whitelabel || company.is_whitelabel_master || company.is_master) && (
              <Button
                variant="secondary"
                onClick={() => window.open(`/?tenant=${company.slug || company.id}&preview=true`, '_blank')}
              >
                Visualizar Landing Page
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => navigate(`/dashboard/companies/${company.id}/edit`, { state: { company } })}
            >
              <IconEditIcon />
              Editar / Gerenciar
            </Button>
          </div>
        }
      />

      <Page.Content>
        {/* Members list with full management */}
        <Table.Wrapper standalone={true} exportFilename="companydetail">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.75rem', borderBottom: '1px solid var(--border)' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-1)' }}>Usuários</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '12.5px', color: 'var(--text-3)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '99px', padding: '2px 10px' }}>
                {members.length} usuário{members.length !== 1 ? 's' : ''}
              </span>
              <Button 
                onClick={() => navigate(`/dashboard/companies/${companyId}/users/new`)} 
                icon={<IconPlus />} 
                style={{ padding: '4px 10px', fontSize: '12.5px' }}
              >
                Adicionar Usuário
              </Button>
            </div>
          </div>

          {loadingMembers ? (
            <div className="ui-empty-state">Carregando usuários...</div>
          ) : members.length === 0 ? (
            <Table.EmptyState
              message="Nenhum usuário adicionado."
              icon={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px', borderRadius: '50%', background: 'var(--primary-light)', color: 'var(--primary)', marginBottom: '.25rem' }}>
                  <IconUser size={32} />
                </div>
              }
              action={
                <Button variant="primary" onClick={() => navigate(`/dashboard/companies/${companyId}/users/new`)}>
                  Adicionar usuário
                </Button>
              }
            />
          ) : (
            <Table>
              <thead>
                <tr>
                  <th style={{ width: 50 }}></th>
                  <th>Nome / E-mail</th>
                  <th>Papel</th>
                  <th style={{ textAlign: 'right', width: 120 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.id}>
                    <td>
                      <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'var(--primary-light)', color: 'var(--primary)', fontSize: '14px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {m.email[0].toUpperCase()}
                      </div>
                    </td>
                    <td className="ui-table__name">
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span>{m.name || m.email.split('@')[0]}</span>
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{m.email}</span>
                      </div>
                    </td>
                    <td>
                      <Table.Badge variant={m.role as string === 'owner' ? 'primary' : m.role === 'admin' ? 'warning' : 'success'}>
                        {ROLE_LABELS[m.role] ?? m.role}
                      </Table.Badge>
                    </td>
                    <td data-label="Ações">
                      <div className="ui-table__actions" style={{ justifyContent: 'flex-end' }}>
                        {m.role as string !== 'owner' && (
                          <>
                            <Table.ActionButton
                              title="Editar"
                              onClick={() => navigate(`/dashboard/companies/${companyId}/users/${m.id}/edit`)}
                              icon={<IconEditIcon />}
                            />
                            <Table.ActionButton
                              danger
                              title="Remover"
                              onClick={() => setMemberToDelete(m)}
                              icon={<IconTrash />}
                            />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Table.Wrapper>
      </Page.Content>

      {/* CONFIRMAÇÃO DE DELEÇÃO */}
      {memberToDelete && (
        <ConfirmModal
          title="Remover Usuário"
          message={`Tem certeza que deseja remover o acesso do usuário "${memberToDelete.name || memberToDelete.email}" desta empresa?`}
          type="error"
          onConfirm={() => { void executeDelete() }}
          onCancel={() => setMemberToDelete(null)}
          confirmText="Remover"
          isDanger
          isLoading={deleting}
        />
      )}

      {/* MODAL DE ERRO */}
      {alertConfig?.isOpen && (
        <AlertModal
          title="Atenção"
          type="error"
          message={alertConfig.message}
          onClose={() => setAlertConfig(null)}
        />
      )}
    </Page>
  )
}
